import { useEffect } from 'react';
import i18n from 'i18next';

import { eraseCookiesDirectly, readCookie, setCookie } from 'utils/Cookies';
import { setCookieBannerFocus } from 'utils/cookieBannerFocus';
import * as Locales from 'utils/Locales';
import { isObject } from 'lodash';
import { isEmptyObject } from 'utils/Object';

const CHECK_INTERVAL = 1000;

/**
 * List of essential cookies that should not be removed.
 */
const essentialCookies = [
  '_dd_s',
  'agentId',
  'bcContactNumber',
  'clAnalyticsId',
  'isCloverMerchant',
  'program',
  'referralType',
  'referrer',
  'salesforceId',
  'salesforcePartnerId',
  '_evidon_consent_cookie',
  '_evidon_suppress_notification_cookie',
];

type Cookie = {
  name: string;
  value: string;
  expires?: number;
  consented?: boolean;
};

/**
 * @typedef {Object} Cookie
 * @property {string} name - The name of the cookie.
 * @property {string} value - The value of the cookie.
 * @property {number} [expires] - The expiration time of the cookie.
 * @property {boolean} [consented] - Whether the cookie is consented.
 */

/**
 * Determines if explicit consent is required based on the user's locale.
 * @returns {boolean} True if explicit consent is required.
 */
export const requiresExplicitConsent = () => {
  const currentLocale = Locales.getLocale(i18n.language);
  return currentLocale.country.code === Locales.CountryCode.AUSTRALIA;
};

/**
 * Waits for Evidon to be loaded on the window object.
 * @returns {Promise<boolean>} Resolves when Evidon is loaded.
 */
export const waitForEvidon = () =>
  new Promise(resolve => {
    if (window.evidon?.notice) return resolve(true);

    const interval = setInterval(() => {
      if (window.evidon?.notice) {
        clearInterval(interval);
        resolve(true);
      }
    }, CHECK_INTERVAL); // Check every 1 second until Evidon is loaded
  });

/**
 * Checks if the user has opted in for analytics cookies.
 * @returns {boolean} True if the user has opted in for analytics.
 */
export const hasUserOptedInForAnalytics = () => {
  const analyticsCategoryId = 'analytics and performance';
  const isAnalyticsConsented =
    `${window?.evidon?.notice?._getConsentedCategories?.()[analyticsCategoryId]}` === 'true';
  const isConsentCookiePresent = Boolean(window?.evidon?.notice?._getConsentCookie());

  if (requiresExplicitConsent()) {
    return isConsentCookiePresent && isAnalyticsConsented;
  }

  if (isConsentCookiePresent) {
    return isAnalyticsConsented;
  }

  return true;
};

/**
 * Handles the setting or deleting of cookies based on user consent.
 */
export const useHandleEvidonCookieConsent = () => {
  /**
   * Handles opting in a cookie by setting it.
   * @param {Cookie} cookie - The cookie to set.
   * @returns {boolean} False if the cookie is successfully set.
   */
  const handleOptInCookie = ({ name, value, expires }: Cookie) => {
    const cookieExists = Boolean(readCookie(name));

    if (cookieExists) {
      return false; // Cookie already exists, no action needed
    }

    try {
      setCookie(name, value, expires);
      return false; // Successfully set, remove from retry list
    } catch (error) {
      console.error(`Error setting cookie ${name}:`, error);
      return true; // Keep for retry if an error occurred
    }
  };

  /**
   * Handles opting out a cookie by deleting it.
   * @param {Cookie} cookie - The cookie to delete.
   * @returns {boolean} True if the cookie should be retried for deletion.
   */
  const handleOptOutCookie = ({ name }: Cookie) => {
    const cookieExists = document.cookie.includes(name);

    if (!cookieExists) {
      return false; // Cookie doesn't exist, no action needed
    }

    try {
      eraseCookiesDirectly(name);
      return true; // Keep in the array for re-check
    } catch (error) {
      console.error(`Error removing cookie ${name}:`, error);
      return true; // Keep for retry if an error occurred
    }
  };

  /**
   * Retrieves the list of cookies to process based on user consent.
   * @returns {Promise<Cookie[]>} List of cookies to process.
   *
   * @description
   * This function determines which cookies need to be set or deleted
   * based on the user's consent preferences. The behavior changes based
   * on the value of `consentedCookies` and `hasOptOutDetails`.
   *
   * **Scenarios:**
   *
   * 1. **`hasOptOutDetails` is `null`**
   *    - `consentedCookies` is `null`.
   *    - `hasOptOutDetails` is `false`.
   *    - No consent data is available.
   *    - All non-essential cookies are considered not consented.
   *    - The function will prepare all non-essential cookies for deletion.
   *
   * 2. **`hasOptOutDetails` is a full object**
   *    - `consentedCookies` is a non-empty object with user selections.
   *    - `hasOptOutDetails` is `true`.
   *    - Consent data is available.
   *    - The function updates `cookiesToProcess` with consented cookies.
   *    - Consented cookies will be set, others will be deleted.
   *
   * 3. **`hasOptOutDetails` is an empty object**
   *    - `consentedCookies` is an empty object `{}`.
   *    - `hasOptOutDetails` is `false`.
   *    - User has made direct selections but hasn't consented to any cookies.
   *    - All non-essential cookies are considered not consented.
   *    - The function will prepare all non-essential cookies for deletion.
   */
  const getCookiesToProcess = async () => {
    const { notice } = window.evidon;

    await new Promise(resolve => {
      notice.getOptOutCookies(resolve);
    });

    // Get all cookies from the document
    const allCookies = document.cookie
      .split('; ')
      .map(c => c.split('='))
      .map(([name, value]) => ({ name, value, consented: false }));

    const consentedCookies = notice._getConsentedCookies() as undefined | null | boolean | object;

    const optOutCookies = notice.optOutDetails?.cookies;

    // Filter out essential cookies
    const cookiesToProcess = allCookies.filter(({ name }) => !essentialCookies.includes(name));

    // If consent data is available, add it to the cookies to process
    const hasOptOutDetails = isObject(consentedCookies) && !isEmptyObject(consentedCookies);

    if (hasOptOutDetails) {
      // Process consented cookies
      Object.entries(consentedCookies)
        .filter(([, consented]) => consented)
        .forEach(([cookieId, consented]) => {
          const cookieDetail = optOutCookies[cookieId];
          const indx = cookiesToProcess.findIndex(({ name }) => name === cookieDetail.name);

          if (indx !== -1) {
            // update consented cookies that are in document.cookies
            cookiesToProcess[indx] = { ...cookieDetail, consented };
          } else {
            // add consented cookies that are not in document.cookies
            cookiesToProcess.push({ ...cookieDetail, consented });
          }
        });
    }

    // SuppressionCookie is set when the user has acknowledged the consent
    const hasAcknowledgedConsent = Boolean(notice._getSuppressionCookie());
    if (hasAcknowledgedConsent && consentedCookies === true) {
      // User has accepted all cookies
      cookiesToProcess.forEach((c, i) => {
        cookiesToProcess[i].consented = true;
      });
    }

    // If no consent data is available, prepare all non-essential cookies
    return cookiesToProcess;
  };

  /**
   * Main function to handle cookie consent processing.
   *
   * It sets or deletes cookies based on the user's consent preferences.
   */
  const handleCookieConsent = () => {
    const interval = setInterval(async () => {
      const cookiesToProcess = await getCookiesToProcess();

      // Process the cookies
      const cookiesToProcessDuringInterval = cookiesToProcess.filter(cookieDetail =>
        cookieDetail.consented ? handleOptInCookie(cookieDetail) : handleOptOutCookie(cookieDetail),
      );

      // If no cookies are left to process (or retry), clear the interval
      if (cookiesToProcessDuringInterval.length === 0) {
        clearInterval(interval);
      }
    }, CHECK_INTERVAL); // Check every 1 second
  };

  useEffect(() => {
    /**
     * Sets up handlers for Evidon events and triggers initial consent processing.
     */
    const setupEvidonHandlers = () => {
      const { notice } = window.evidon;
      const consentedCookies = notice._getConsentedCookies() as undefined | null | boolean | object;

      /**
       * Scenarios for `consentedCookies` and their effects:
       *
       * 1. **`consentedCookies` is `null` (User declined all cookies or hasn't made a selection)**
       *    - `hasOptOutDetails` is set to `true` because `consentedCookies === null`.
       *    - Since `hasOptOutDetails` is `true`, `handleCookieConsent()` is scheduled to run after a delay.
       *    - The `handleCookieConsent()` function will process cookie consent,
       *      typically deleting all non-essential cookies.
       *
       * 2. **`consentedCookies` is a non-empty object (User consented to some cookies)**
       *    - `hasOptOutDetails` is set to `true` because `consentedCookies` is an object and not empty.
       *    - `handleCookieConsent()` is scheduled to run after a delay.
       *    - The function will set or delete cookies based on the user's specific consent selections.
       *
       * 3. **`consentedCookies` is an empty object `{}` (User has not consented to any cookies)**
       *    - `hasOptOutDetails` is set to `false` because `consentedCookies` is an empty object.
       *    - `handleCookieConsent()` will only be called if `requiresExplicitConsent()` returns `true`
       *      (e.g., for regions requiring explicit consent like Australia).
       *    - If called, the function will proceed to delete all non-essential cookies.
       *    - If not called, non-essential cookies may remain, which could be non-compliant with consent regulations.
       */

      const originalConsentChanged = window.evidon.notice.consentChanged;
      window.evidon.notice.consentChanged = function () {
        originalConsentChanged.apply(this, arguments); // eslint-disable-line prefer-rest-params
        handleCookieConsent();
      };

      const originalConsentGiven = window.evidon.notice.consentGiven;
      window.evidon.notice.consentGiven = function () {
        originalConsentGiven.apply(this, arguments); // eslint-disable-line prefer-rest-params
        handleCookieConsent();
      };

      const originalDeclineGiven = window.evidon.notice.declineGiven;
      window.evidon.notice.declineGiven = function () {
        originalDeclineGiven.apply(this, arguments); // eslint-disable-line prefer-rest-params
        handleCookieConsent();
      };

      const originalPriorConsentCallback = window.evidon.priorConsentCallback;
      window.evidon.priorConsentCallback = function () {
        originalPriorConsentCallback.apply(this, arguments); // eslint-disable-line prefer-rest-params
        handleCookieConsent();
      };

      // for opt-in countries, handle cookie consent on page load
      // also checks if there are any OptOutDetails
      const hasOptOutDetails =
        consentedCookies === null || (isObject(consentedCookies) && !isEmptyObject(consentedCookies));
      if (requiresExplicitConsent() || hasOptOutDetails) {
        setTimeout(() => handleCookieConsent(), CHECK_INTERVAL);
      }
    };

    waitForEvidon().then(() => {
      setCookieBannerFocus();
      setupEvidonHandlers();
    });
  }, [requiresExplicitConsent()]);
};
